'use client'

import {
  UserCheck,
  User,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BookOpen,
  MessageSquare,
  Bot,
  Wrench,
  Shield,
  Brain,
  Eye,
} from 'lucide-react'

/**
 * Junior Developer Extinction Diagrams
 * Visual components for the junior dev pipeline crisis blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// Talent Pipeline Crisis Diagram
export function TalentPipelineCrisisDiagram() {
  const levels = [
    {
      label: 'Senior Engineers (10+ yr)',
      status: 'Healthy supply',
      fill: 85,
      color: '#008dcc',
      Icon: UserCheck,
      statusColor: '#22c55e',
    },
    {
      label: 'Mid-Level by 2028',
      status: 'Critically thin',
      fill: 25,
      color: '#FDC304',
      Icon: AlertTriangle,
      statusColor: '#f97316',
    },
    {
      label: 'Juniors Hired 2024-2026',
      status: 'Nearly empty',
      fill: 10,
      color: '#ef4444',
      Icon: User,
      statusColor: '#ef4444',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE TALENT PIPELINE CRISIS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Where future mid-level and senior engineers come from
        </p>
      </div>

      <div className='flex flex-col items-center gap-4'>
        {/* Pipeline visualization */}
        <div className='w-full max-w-lg space-y-4'>
          {levels.map((level, idx) => (
            <div key={idx} className='space-y-2'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <level.Icon
                    className='w-4 h-4'
                    style={{ color: level.color }}
                  />
                  <span className='text-xs md:text-sm font-medium text-foreground'>
                    {level.label}
                  </span>
                </div>
                <span
                  className='text-xs font-bold'
                  style={{ color: level.statusColor }}
                >
                  {level.status}
                </span>
              </div>
              <div className='relative h-8 bg-muted rounded-lg overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-lg flex items-center justify-end pr-3 transition-all'
                  style={{
                    width: `${level.fill}%`,
                    backgroundColor: `${level.color}40`,
                    borderRight: `3px solid ${level.color}`,
                  }}
                >
                  {level.fill > 15 && (
                    <span
                      className='text-[10px] font-bold'
                      style={{ color: level.color }}
                    >
                      {level.fill}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Connecting arrows */}
        <div className='flex items-center gap-2 text-muted-foreground'>
          <TrendingDown className='w-4 h-4' />
          <span className='text-xs'>Pipeline narrows over time</span>
        </div>

        {/* Key stats */}
        <div className='grid grid-cols-3 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-red-500'>54%</p>
            <p className='text-[10px] text-muted-foreground'>
              Leaders hiring fewer juniors
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#FDC304]/10 border border-[#FDC304]/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-[#FDC304]'>
              3-5 yr
            </p>
            <p className='text-[10px] text-muted-foreground'>
              To grow into mid-level
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-[#008dcc]'>2028</p>
            <p className='text-[10px] text-muted-foreground'>
              When the gap becomes critical
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-red-500/10 rounded-lg border border-red-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The math is simple:</span> Seniors
            who retire or leave in 2028 cannot be replaced by mid-levels who
            were never hired as juniors in 2024.
          </p>
        </div>
      </div>
    </div>
  )
}

// Apprenticeship ROI Comparison Diagram
export function ApprenticeshipROIDiagram() {
  const cutPath = [
    {
      year: 'Year 1',
      label: 'Save on headcount',
      sentiment: 'positive',
      barWidth: 70,
      color: '#22c55e',
    },
    {
      year: 'Year 2',
      label: 'Debt compounds silently',
      sentiment: 'warning',
      barWidth: 45,
      color: '#FDC304',
    },
    {
      year: 'Year 3',
      label: 'Consultant cleanup',
      sentiment: 'negative',
      barWidth: 20,
      color: '#ef4444',
    },
  ]

  const investPath = [
    {
      year: 'Year 1',
      label: 'Investment in training',
      sentiment: 'investment',
      barWidth: 30,
      color: '#f97316',
    },
    {
      year: 'Year 2',
      label: 'Contributing meaningfully',
      sentiment: 'growing',
      barWidth: 60,
      color: '#008dcc',
    },
    {
      year: 'Year 3',
      label: 'Mid-level ready',
      sentiment: 'positive',
      barWidth: 85,
      color: '#22c55e',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          3-YEAR COST COMPARISON
        </h3>
        <p className='text-sm text-muted-foreground'>
          What happens when you cut junior hiring vs. invest in apprenticeships
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
          {/* Cut Juniors path */}
          <div className='p-4 rounded-xl border-2 border-red-500 bg-red-500/5'>
            <div className='flex items-center gap-2 mb-4'>
              <div className='w-8 h-8 rounded-full bg-red-500 flex items-center justify-center'>
                <TrendingDown className='w-4 h-4 text-white' />
              </div>
              <div>
                <p className='font-bold text-sm text-foreground'>
                  Cut Junior Hiring
                </p>
                <p className='text-[10px] text-muted-foreground'>
                  Short-term savings, long-term pain
                </p>
              </div>
            </div>
            <div className='space-y-3'>
              {cutPath.map((step, idx) => (
                <div key={idx} className='space-y-1'>
                  <div className='flex items-center justify-between text-xs'>
                    <span className='font-medium text-foreground'>
                      {step.year}
                    </span>
                    <span className='text-muted-foreground'>{step.label}</span>
                  </div>
                  <div className='relative h-5 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='absolute top-0 left-0 h-full rounded-full'
                      style={{
                        width: `${step.barWidth}%`,
                        backgroundColor: step.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className='mt-3 p-2 rounded-lg bg-red-500/10 text-center'>
              <p className='text-[10px] text-red-500 font-bold'>
                NET RESULT: Negative ROI by Year 3
              </p>
            </div>
          </div>

          {/* Invest in Juniors path */}
          <div className='p-4 rounded-xl border-2 border-[#22c55e] bg-[#22c55e]/5'>
            <div className='flex items-center gap-2 mb-4'>
              <div className='w-8 h-8 rounded-full bg-[#22c55e] flex items-center justify-center'>
                <TrendingUp className='w-4 h-4 text-white' />
              </div>
              <div>
                <p className='font-bold text-sm text-foreground'>
                  Invest in Apprenticeships
                </p>
                <p className='text-[10px] text-muted-foreground'>
                  Front-loaded cost, compounding value
                </p>
              </div>
            </div>
            <div className='space-y-3'>
              {investPath.map((step, idx) => (
                <div key={idx} className='space-y-1'>
                  <div className='flex items-center justify-between text-xs'>
                    <span className='font-medium text-foreground'>
                      {step.year}
                    </span>
                    <span className='text-muted-foreground'>{step.label}</span>
                  </div>
                  <div className='relative h-5 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='absolute top-0 left-0 h-full rounded-full'
                      style={{
                        width: `${step.barWidth}%`,
                        backgroundColor: step.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className='mt-3 p-2 rounded-lg bg-[#22c55e]/10 text-center'>
              <p className='text-[10px] text-[#22c55e] font-bold'>
                NET RESULT: Positive ROI by Year 2
              </p>
            </div>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The hidden cost:</span> Consultant
            cleanups typically cost more than 3 years of junior salaries
            combined, with none of the institutional knowledge retention.
          </p>
        </div>
      </div>
    </div>
  )
}

// Hiring Decision Matrix Diagram
export function HiringDecisionMatrixDiagram() {
  const quadrants = [
    {
      position: 'Sunset',
      xLabel: 'AI Can Handle',
      yLabel: 'Traditional Tasks',
      color: '#ef4444',
      Icon: Bot,
      items: ['Boilerplate CRUD', 'Config scaffolding', 'Simple unit tests'],
      action: 'Phase out junior allocation',
    },
    {
      position: 'Evolve',
      xLabel: 'AI Can Handle',
      yLabel: 'New Tasks',
      color: '#FDC304',
      Icon: Wrench,
      items: ['AI-assisted testing', 'Generated docs review', 'Prompt tuning'],
      action: 'Retrain juniors for oversight',
    },
    {
      position: 'Keep',
      xLabel: 'AI Cannot Handle',
      yLabel: 'Traditional Tasks',
      color: '#008dcc',
      Icon: Shield,
      items: [
        'Architecture review',
        'Cross-team debugging',
        'Incident response',
      ],
      action: 'Preserve and mentor into',
    },
    {
      position: 'Create New',
      xLabel: 'AI Cannot Handle',
      yLabel: 'New Tasks',
      color: '#22c55e',
      Icon: Eye,
      items: ['AI output review', 'Debt archaeology', 'Pattern challenging'],
      action: 'Build new junior roles here',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE HIRING DECISION MATRIX
        </h3>
        <p className='text-sm text-muted-foreground'>
          Where to deploy junior developers in an AI-native engineering org
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* 2x2 grid */}
        <div className='grid grid-cols-2 gap-3 w-full max-w-lg'>
          {quadrants.map((q, idx) => (
            <div
              key={idx}
              className='p-4 rounded-xl bg-background border border-border/60'
            >
              <div className='flex items-center gap-2 mb-3'>
                <div
                  className='w-7 h-7 rounded-full flex items-center justify-center'
                  style={{ backgroundColor: q.color }}
                >
                  <q.Icon className='w-3.5 h-3.5 text-white' />
                </div>
                <span className='text-sm font-bold' style={{ color: q.color }}>
                  {q.position}
                </span>
              </div>
              <ul className='space-y-1 mb-3'>
                {q.items.map((item, i) => (
                  <li key={i} className='flex items-start gap-1.5'>
                    <div
                      className='w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0'
                      style={{ backgroundColor: q.color }}
                    />
                    <span className='text-[10px] md:text-xs text-muted-foreground'>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              <div
                className='px-2 py-1 rounded text-[10px] font-medium text-center'
                style={{
                  backgroundColor: `${q.color}15`,
                  color: q.color,
                }}
              >
                {q.action}
              </div>
            </div>
          ))}
        </div>

        {/* Axis labels */}
        <div className='grid grid-cols-2 gap-4 w-full max-w-lg'>
          <div className='flex items-center gap-2 justify-center'>
            <div className='w-3 h-3 rounded bg-muted border border-border/60' />
            <span className='text-[10px] text-muted-foreground'>
              Left col: AI can handle
            </span>
          </div>
          <div className='flex items-center gap-2 justify-center'>
            <div className='w-3 h-3 rounded bg-muted border border-border/60' />
            <span className='text-[10px] text-muted-foreground'>
              Right col: AI cannot handle
            </span>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#22c55e]/10 rounded-lg border border-[#22c55e]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The opportunity:</span> Two of the
            four quadrants represent entirely new roles that did not exist
            before AI coding tools.
          </p>
        </div>
      </div>
    </div>
  )
}

// Knowledge Transfer Breakdown Diagram
export function KnowledgeTransferBreakdownDiagram() {
  const knowledgeTypes = [
    {
      type: 'Documented Knowledge',
      description: 'README files, API docs, architecture diagrams',
      withJuniors: 85,
      withoutJuniors: 70,
      color: '#22c55e',
      Icon: BookOpen,
      decayNote: 'Slow decay: docs go stale without questions forcing updates',
    },
    {
      type: 'Tribal Knowledge',
      description: 'Why we use X instead of Y, historical context',
      withJuniors: 75,
      withoutJuniors: 30,
      color: '#FDC304',
      Icon: MessageSquare,
      decayNote:
        'Fast decay: leaves when seniors leave, only survives if someone asks',
    },
    {
      type: 'Contextual Knowledge',
      description: 'Edge cases, failure modes, "that one time in production"',
      withJuniors: 65,
      withoutJuniors: 15,
      color: '#F90B8A',
      Icon: Brain,
      decayNote:
        'Critical decay: only transfers through mentorship and pairing',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          KNOWLEDGE TRANSFER BREAKDOWN
        </h3>
        <p className='text-sm text-muted-foreground'>
          How institutional knowledge decays without junior developers asking
          questions
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Knowledge bars */}
        <div className='w-full max-w-lg space-y-5'>
          {knowledgeTypes.map((k, idx) => (
            <div key={idx} className='space-y-2'>
              <div className='flex items-center gap-2'>
                <k.Icon className='w-4 h-4' style={{ color: k.color }} />
                <span className='text-xs md:text-sm font-bold text-foreground'>
                  {k.type}
                </span>
              </div>
              <p className='text-[10px] text-muted-foreground ml-6'>
                {k.description}
              </p>

              {/* With juniors bar */}
              <div className='ml-6 space-y-1'>
                <div className='flex items-center gap-2'>
                  <span className='text-[10px] text-muted-foreground w-24'>
                    With juniors
                  </span>
                  <div className='flex-1 relative h-5 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='absolute top-0 left-0 h-full rounded-full flex items-center justify-end pr-2'
                      style={{
                        width: `${k.withJuniors}%`,
                        backgroundColor: `${k.color}60`,
                      }}
                    >
                      <span
                        className='text-[10px] font-bold'
                        style={{ color: k.color }}
                      >
                        {k.withJuniors}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Without juniors bar */}
                <div className='flex items-center gap-2'>
                  <span className='text-[10px] text-muted-foreground w-24'>
                    Without juniors
                  </span>
                  <div className='flex-1 relative h-5 bg-muted rounded-full overflow-hidden'>
                    <div
                      className='absolute top-0 left-0 h-full rounded-full flex items-center justify-end pr-2'
                      style={{
                        width: `${k.withoutJuniors}%`,
                        backgroundColor: `${k.color}30`,
                      }}
                    >
                      <span className='text-[10px] font-bold text-foreground/60'>
                        {k.withoutJuniors}%
                      </span>
                    </div>
                  </div>
                </div>

                <p className='text-[10px] italic text-muted-foreground'>
                  {k.decayNote}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className='flex items-center justify-center gap-6'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-[#22c55e]/60' />
            <span className='text-xs text-muted-foreground'>
              Retained with juniors
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-[#22c55e]/30' />
            <span className='text-xs text-muted-foreground'>
              Retained without juniors
            </span>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#F90B8A]/10 rounded-lg border border-[#F90B8A]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The question paradox:</span> Junior
            developers asking "why?" is the primary mechanism that converts
            tribal and contextual knowledge into documented knowledge.
          </p>
        </div>
      </div>
    </div>
  )
}
